import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useHealth, buildSummary } from "@/context/HealthContext";
import { HCard, RiskBadge, RISK_BAR } from "@/components/health-kit";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ClipboardCheck, Check, Pencil, X, AlertTriangle, ShieldCheck, HelpCircle, Info } from "lucide-react";

const RISKS = ["stable", "moderate", "elevated", "attention"];
const REASONS = ["clinical_context", "insufficient_evidence", "data_quality", "alternative_explanation", "other"];

export default function ProfessionalReview() {
  const { ht, refresh, tick } = useHealth();
  const { user } = useAuth();
  const isPro = user?.role === "healthcare_professional";
  const [queue, setQueue] = useState([]);
  const [active, setActive] = useState(null);
  const [mode, setMode] = useState(null); // CORRECT | REJECT | null
  const [correctedRisk, setCorrectedRisk] = useState("moderate");
  const [reason, setReason] = useState("clinical_context");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/health/analyses?status=pending_review").then(({ data }) => setQueue(data)).catch(() => {});
  useEffect(() => { load(); }, [tick]);

  const open = async (a) => {
    setActive(a); setMode(null); setNote(""); setReason("clinical_context"); setCorrectedRisk(a.risk_level);
    try { await api.post(`/health/analyses/${a.id}/open`); } catch {}
  };

  const decide = async (decision) => {
    if (!isPro) { toast.error(ht("onlyProfessional")); return; }
    setBusy(true);
    try {
      await api.post(`/health/analyses/${active.id}/review`, {
        decision,
        corrected_risk_level: decision === "CORRECT" ? correctedRisk : null,
        reason: (decision === "CORRECT" || decision === "REJECT") ? reason : null,
        note: note || null,
      });
      toast.success(`${ht("decision")}: ${ht(decision === "APPROVE" ? "approve" : decision === "CORRECT" ? "correct" : "reject")}`);
      setActive(null); refresh(); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 ngx-fade-in" data-testid="health-review">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-50">{ht("nav_review")}</h1>
          <p className="text-sm text-slate-400 mt-1">{ht("aiSupports")}</p>
        </div>
        {!isPro && (
          <span className="flex items-center gap-2 text-xs text-amber-400 px-3 py-1.5 rounded-sm bg-amber-950/30 border border-amber-800" data-testid="review-role-note">
            <Info className="h-3.5 w-3.5" /> {ht("onlyProfessional")}
          </span>
        )}
      </div>

      {queue.length === 0 ? (
        <HCard className="p-10 text-center text-slate-500" data-testid="review-empty">
          <ClipboardCheck className="h-8 w-8 mx-auto mb-3 text-slate-600" /> {ht("noPending")}
        </HCard>
      ) : (
        <div className="space-y-3" data-testid="review-queue">
          {queue.map((a) => (
            <HCard key={a.id} className="p-4 flex items-center gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <span className="font-mono text-slate-100 font-semibold">{a.patient_id}</span>
                <RiskBadge level={a.risk_level} ht={ht} />
              </div>
              <span className="text-xs text-slate-500 font-mono truncate">{(a.signals || []).length} {ht("potentialSignals")} · {ht("confidence")} {a.confidence}%</span>
              <Button onClick={() => open(a)} data-testid={`review-open-${a.id}`} className="ml-auto h-9 bg-teal-600 hover:bg-teal-700 text-white text-sm">
                {ht("review")}
              </Button>
            </HCard>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-[#0A1620] border-slate-700 text-slate-100 max-w-2xl p-0 gap-0" data-testid="review-modal">
          {active && (
            <>
              <DialogHeader className="p-5 border-b border-slate-800">
                <DialogTitle className="flex items-center gap-3">
                  <span className="font-mono text-lg">{active.patient_id}</span>
                  <RiskBadge level={active.risk_level} ht={ht} />
                  <span className="ml-auto text-xs font-mono text-slate-500">{ht("confidence")}: {active.confidence}%</span>
                </DialogTitle>
              </DialogHeader>

              <div className="p-5 space-y-4 max-h-[55vh] overflow-y-auto">
                <div>
                  <div className="text-xs uppercase tracking-wider text-teal-400 mb-1">{ht("aiSummary")}</div>
                  <p className="text-sm text-slate-200">{buildSummary(active, ht)}</p>
                  <p className="text-[11px] text-slate-500 mt-1">{ht("confidenceNote")}</p>
                </div>

                <div className="rounded-md border border-amber-900/50 bg-amber-950/10 p-4">
                  <div className="flex items-center gap-2 text-amber-300 mb-3">
                    <HelpCircle className="h-4 w-4" /><span className="text-sm font-semibold">{ht("whyFlagged")}</span>
                  </div>
                  {(active.signals || []).length === 0 && <p className="text-sm text-slate-400">—</p>}
                  <ul className="space-y-2">
                    {(active.signals || []).map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-200">
                        <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                        <span>
                          {ht("sig_" + s.code)}
                          <SignalDetail s={s} ht={ht} />
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {mode === "CORRECT" && (
                  <div className="grid grid-cols-2 gap-3 ngx-fade-in">
                    <Field label={ht("correctedRisk")}>
                      <Select value={correctedRisk} onValueChange={setCorrectedRisk}>
                        <SelectTrigger data-testid="corrected-risk" className="h-10 bg-[#050C11] border-slate-700"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#0A1620] border-slate-700 text-slate-100">{RISKS.map((r) => <SelectItem key={r} value={r}>{ht("risk_" + r)}</SelectItem>)}</SelectContent>
                      </Select>
                    </Field>
                    <Field label={ht("reasonCorrection")}>
                      <ReasonSelect reason={reason} setReason={setReason} ht={ht} />
                    </Field>
                  </div>
                )}
                {mode === "REJECT" && (
                  <Field label={ht("reasonRejection")}><ReasonSelect reason={reason} setReason={setReason} ht={ht} /></Field>
                )}

                <Field label={ht("professionalNote")}>
                  <Textarea data-testid="professional-note" value={note} onChange={(e) => setNote(e.target.value)}
                    className="bg-[#050C11] border-slate-700 text-slate-100 min-h-[70px]" />
                </Field>
              </div>

              <div className="p-5 border-t border-slate-800">
                <div className="flex items-center gap-2 mb-3 text-teal-400 text-xs font-semibold uppercase tracking-widest">
                  <ShieldCheck className="h-4 w-4" /> {ht("professionalGate")}
                </div>
                {mode === null ? (
                  <div className="grid grid-cols-3 gap-3">
                    <Button data-testid="review-approve" disabled={busy || !isPro} onClick={() => decide("APPROVE")}
                      className={`h-14 flex-col gap-1 ${isPro ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-800 border border-slate-700"} text-white disabled:opacity-50`}><Check className="h-4 w-4" /> {ht("approve")}</Button>
                    <Button data-testid="review-correct" disabled={busy || !isPro} onClick={() => setMode("CORRECT")}
                      className={`h-14 flex-col gap-1 ${isPro ? "bg-teal-600 hover:bg-teal-700" : "bg-slate-800 border border-slate-700"} text-white disabled:opacity-50`}><Pencil className="h-4 w-4" /> {ht("correct")}</Button>
                    <Button data-testid="review-reject" disabled={busy || !isPro} onClick={() => setMode("REJECT")}
                      className={`h-14 flex-col gap-1 ${isPro ? "bg-red-600 hover:bg-red-700" : "bg-slate-800 border border-slate-700"} text-white disabled:opacity-50`}><X className="h-4 w-4" /> {ht("reject")}</Button>
                  </div>
                ) : (
                  <div className="flex gap-3">
                    <Button onClick={() => setMode(null)} className="flex-1 h-11 bg-slate-800 hover:bg-slate-700 text-slate-200">{ht("cancel")}</Button>
                    <Button data-testid="review-submit" disabled={busy} onClick={() => decide(mode)}
                      className="flex-1 h-11 bg-teal-600 hover:bg-teal-700 text-white">{ht("submitDecision")}</Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="text-xs uppercase tracking-wider text-slate-400">{label}</label><div className="mt-1.5">{children}</div></div>;
}
function ReasonSelect({ reason, setReason, ht }) {
  return (
    <Select value={reason} onValueChange={setReason}>
      <SelectTrigger data-testid="review-reason" className="h-10 bg-[#050C11] border-slate-700"><SelectValue /></SelectTrigger>
      <SelectContent className="bg-[#0A1620] border-slate-700 text-slate-100">{REASONS.map((r) => <SelectItem key={r} value={r}>{ht("rs_" + r)}</SelectItem>)}</SelectContent>
    </Select>
  );
}
function SignalDetail({ s, ht }) {
  const d = s.detail || {};
  if (s.code === "hydration_decreasing") return <span className="text-slate-500 font-mono text-xs"> ({d.from}% → {d.to}%)</span>;
  if (s.code === "weight_variation") return <span className="text-slate-500 font-mono text-xs"> (Δ {d.drop} {d.unit})</span>;
  if (s.code === "biomarker_trend_changed") return <span className="text-slate-500 font-mono text-xs"> ({d.name}: {d.from} → {d.to} {d.unit})</span>;
  if (s.code === "vital_pattern_changed") return <span className="text-slate-500 font-mono text-xs"> ({d.metric}: {d.from} → {d.to}{d.unit})</span>;
  return null;
}
